import { useEffect, useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { apiFetch } from "@/lib/api-client";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton, SkeletonCard } from "@/components/skeleton";
import { BarChart3, Clock3, Euro, Mail, MousePointerClick, Target, Trophy } from "lucide-react";

interface CampaignRevenueStats {
  campaignId: string;
  campaignName: string;
  revenueCents: number;
  conversions: number;
  revenuePerEmailCents: number;
  sent: number;
  opened: number;
  clicked: number;
  sentAt: string | null;
}

interface EmailRevenueSummary {
  totalRevenueCents: number;
  totalSent: number;
  totalOpened: number;
  totalClicked: number;
  totalConversions: number;
  revenuePerEmailCents: number;
  revenuePerClickCents: number;
}

interface EmailRevenueResponse {
  data: {
    from: string;
    to: string;
    summary: EmailRevenueSummary;
    campaigns: CampaignRevenueStats[];
  };
}

interface SendTimeResponse {
  recommendation: {
    optimalHour: number;
    confidence: "high" | "medium" | "low";
    distribution: Array<{ hour: number; score: number }>;
  } | null;
  heatmap: Array<{
    day: number;
    hour: number;
    deliveredCount: number;
    openCount: number;
    openRate: number;
  }>;
  provenance: {
    dataStatus: "sufficient" | "insufficient" | "none";
    daysSinceFirstTouch: number | null;
    message: string;
  };
}

interface Performer {
  label: string;
  campaign: CampaignRevenueStats | null;
  value: string;
  hint: string;
}

function formatDateInput(value: Date): string {
  return value.toISOString().slice(0, 10);
}

function getDefaultTo(): string {
  return formatDateInput(new Date());
}

function getDefaultFrom(): string {
  const current = new Date();
  current.setUTCDate(current.getUTCDate() - 29);
  return formatDateInput(current);
}

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);
}

function formatRate(numerator: number, denominator: number): string {
  return denominator > 0 ? `${((numerator / denominator) * 100).toFixed(1)}%` : "0%";
}

function rateValue(numerator: number, denominator: number): number {
  return denominator > 0 ? numerator / denominator : 0;
}

function pickBest(
  stats: CampaignRevenueStats[],
  score: (campaign: CampaignRevenueStats) => number,
): CampaignRevenueStats | null {
  return stats.reduce<CampaignRevenueStats | null>((best, campaign) => {
    if (!best) return campaign;
    return score(campaign) > score(best) ? campaign : best;
  }, null);
}

function getBestPerformers(stats: CampaignRevenueStats[]): Performer[] {
  const bestRevenue = pickBest(stats, (campaign) => campaign.revenueCents);
  const bestOpenRate = pickBest(stats, (campaign) => rateValue(campaign.opened, campaign.sent));
  const bestClickRate = pickBest(stats, (campaign) => rateValue(campaign.clicked, campaign.opened || campaign.sent));

  return [
    {
      label: "Best Revenue",
      campaign: bestRevenue,
      value: bestRevenue ? formatCurrency(bestRevenue.revenueCents) : "€0",
      hint: bestRevenue ? `${bestRevenue.conversions} influenced deals` : "No revenue yet",
    },
    {
      label: "Best Open Rate",
      campaign: bestOpenRate,
      value: bestOpenRate ? formatRate(bestOpenRate.opened, bestOpenRate.sent) : "0%",
      hint: bestOpenRate ? `${bestOpenRate.opened} opens from ${bestOpenRate.sent} sent` : "No opens yet",
    },
    {
      label: "Best Click Rate",
      campaign: bestClickRate,
      value: bestClickRate ? formatRate(bestClickRate.clicked, bestClickRate.opened || bestClickRate.sent) : "0%",
      hint: bestClickRate ? `${bestClickRate.clicked} clicks from mounted tracking` : "No clicks yet",
    },
  ];
}

/** Email analytics page — campaign performance stats. */
export default function EmailAnalyticsPage() {
  const [dateRange, setDateRange] = useState(() => ({
    from: getDefaultFrom(),
    to: getDefaultTo(),
  }));
  const [summary, setSummary] = useState<EmailRevenueSummary | null>(null);
  const [stats, setStats] = useState<CampaignRevenueStats[]>([]);
  const [sendTime, setSendTime] = useState<SendTimeResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const query = useMemo(
    () => new URLSearchParams({ from: dateRange.from, to: dateRange.to }).toString(),
    [dateRange.from, dateRange.to],
  );
  const performers = useMemo(() => getBestPerformers(stats), [stats]);
  const topSendHours = useMemo(
    () => [...(sendTime?.recommendation?.distribution ?? [])].sort((a, b) => b.score - a.score).slice(0, 3),
    [sendTime?.recommendation?.distribution],
  );

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setError(null);
    Promise.all([
      apiFetch<EmailRevenueResponse>(`/email-marketing/analytics/revenue?${query}`),
      apiFetch<{ data: SendTimeResponse }>("/email-marketing/send-time").catch(() => ({ data: null })),
    ])
      .then(([res, sendTimeRes]) => {
        if (!isMounted) return;
        setSummary(res.data.summary);
        setStats(
          (res.data.campaigns ?? []).map((campaign) => ({
            ...campaign,
            revenuePerEmailCents: campaign.sent > 0 ? Math.round(campaign.revenueCents / campaign.sent) : 0,
          })),
        );
        setSendTime(sendTimeRes.data);
      })
      .catch(() => {
        if (!isMounted) return;
        setError("Failed to load email revenue attribution");
        setSummary(null);
        setStats([]);
        setSendTime(null);
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [query]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <SkeletonCard key={i} className="h-24 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (stats.length === 0) {
    return (
      <EmptyState
        icon={<BarChart3 className="h-8 w-8" />}
        title="No analytics yet"
        description="Send your first email campaign to see revenue attribution and performance metrics here."
        actionLabel="Create campaign"
        actionPath="/email/campaigns"
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Email Analytics</h1>
          <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
            Mounted campaign revenue attribution from real sent-email and closed-deal data on the canonical email seam.
          </p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="rounded-xl border border-border bg-card px-4 py-3 text-sm">
            <span className="text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground">From</span>
            <input
              type="date"
              value={dateRange.from}
              onChange={(event) => setDateRange((current) => ({ ...current, from: event.target.value }))}
              className="mt-2 w-full bg-transparent font-medium text-foreground outline-none"
            />
          </label>
          <label className="rounded-xl border border-border bg-card px-4 py-3 text-sm">
            <span className="text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground">To</span>
            <input
              type="date"
              value={dateRange.to}
              onChange={(event) => setDateRange((current) => ({ ...current, to: event.target.value }))}
              className="mt-2 w-full bg-transparent font-medium text-foreground outline-none"
            />
          </label>
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
        <MetricCard icon={<Euro className="h-4 w-4" />} label="Attributed Revenue" value={formatCurrency(summary?.totalRevenueCents ?? 0)} />
        <MetricCard icon={<Mail className="h-4 w-4" />} label="Emails Sent" value={(summary?.totalSent ?? 0).toLocaleString()} sub={summary ? formatCurrency(summary.revenuePerEmailCents) : "€0"} />
        <MetricCard icon={<MousePointerClick className="h-4 w-4" />} label="Clicks" value={(summary?.totalClicked ?? 0).toLocaleString()} sub={summary ? formatCurrency(summary.revenuePerClickCents) : "€0"} color="text-[var(--color-primary)]" />
        <MetricCard icon={<Target className="h-4 w-4" />} label="Influenced Deals" value={(summary?.totalConversions ?? 0).toLocaleString()} />
        <MetricCard icon={<BarChart3 className="h-4 w-4" />} label="Open Rate" value={formatRate(summary?.totalOpened ?? 0, summary?.totalSent ?? 0)} />
      </div>

      {/* Best performers + optimization */}
      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="mb-4 flex items-center gap-2">
            <Trophy className="h-4 w-4 text-[var(--color-warning)]" />
            <h2 className="text-lg font-semibold text-foreground">Best Performing Campaigns</h2>
          </div>
          <div className="grid gap-3 md:grid-cols-3">
            {performers.map((performer) => (
              <div key={performer.label} className="stagger-item rounded-lg border border-border bg-background px-4 py-3">
                <p className="text-xs font-medium uppercase text-muted-foreground">{performer.label}</p>
                <p className="mt-1 truncate text-sm font-semibold text-foreground">
                  {performer.campaign?.campaignName ?? "No campaign yet"}
                </p>
                <p className="mt-2 text-2xl font-bold text-foreground">{performer.value}</p>
                <p className="mt-1 text-xs text-muted-foreground">{performer.hint}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-lg border border-border bg-card p-4">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Clock3 className="h-4 w-4 text-[var(--color-primary)]" />
              <h2 className="text-lg font-semibold text-foreground">Send-Time Optimization</h2>
            </div>
            {sendTime?.recommendation ? (
              <span className="rounded-full border border-border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                {sendTime.recommendation.confidence}
              </span>
            ) : null}
          </div>
          {sendTime?.recommendation ? (
            <>
              <p className="text-3xl font-bold text-foreground">
                {String(sendTime.recommendation.optimalHour).padStart(2, "0")}:00
              </p>
              <p className="mt-1 text-sm text-muted-foreground">{sendTime.provenance.message}</p>
              <div className="mt-4 space-y-2">
                {topSendHours.map((entry) => (
                  <div key={entry.hour} className="stagger-item flex items-center gap-3">
                    <span className="w-12 text-xs font-medium text-muted-foreground">
                      {String(entry.hour).padStart(2, "0")}:00
                    </span>
                    <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
                      <div
                        className="h-full rounded-full bg-[var(--color-primary)]"
                        style={{ width: `${Math.max(6, Math.min(100, entry.score))}%` }}
                      />
                    </div>
                    <span className="w-10 text-right text-xs text-muted-foreground">{Math.round(entry.score)}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              {sendTime?.provenance?.message ?? "Send-time recommendations will appear once engagement data is available."}
            </p>
          )}
        </div>
      </div>

      {/* Per-campaign breakdown */}
      <div>
        <h2 className="mb-3 text-lg font-semibold text-foreground">Campaign Revenue Attribution</h2>
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/50 text-left text-xs font-medium uppercase text-muted-foreground">
                <th className="px-4 py-3">Campaign</th>
                <th className="px-4 py-3">Sent</th>
                <th className="px-4 py-3">Open Rate</th>
                <th className="px-4 py-3">Click Rate</th>
                <th className="px-4 py-3">Conversions</th>
                <th className="px-4 py-3">Revenue</th>
                <th className="px-4 py-3">Revenue / Email</th>
                <th className="px-4 py-3">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {stats.map((s) => (
                <tr key={s.campaignId} className="stagger-item hover:bg-accent/50">
                  <td className="px-4 py-3 font-medium text-foreground">{s.campaignName}</td>
                  <td className="px-4 py-3 text-muted-foreground">{s.sent}</td>
                  <td className="px-4 py-3 text-[var(--color-success)]">{formatRate(s.opened, s.sent)}</td>
                  <td className="px-4 py-3 text-[var(--color-primary)]">{formatRate(s.clicked, s.opened || s.sent)}</td>
                  <td className="px-4 py-3 text-foreground">{s.conversions}</td>
                  <td className="px-4 py-3 font-medium text-foreground">{formatCurrency(s.revenueCents)}</td>
                  <td className="px-4 py-3 text-muted-foreground">{formatCurrency(s.revenuePerEmailCents)}</td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {s.sentAt ? new Date(s.sentAt).toLocaleDateString() : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function MetricCard({ icon, label, value, sub, color }: {
  icon: React.ReactNode; label: string; value: string; sub?: string; color?: string;
}) {
  return (
    <div className="stagger-item rounded-lg border border-border bg-card p-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-medium uppercase text-muted-foreground">{label}</p>
        <div className="text-muted-foreground">{icon}</div>
      </div>
      <p className={cn("mt-1 text-2xl font-bold", color ?? "text-foreground")}>
        {value}
      </p>
      {sub && <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}
